/**
 * FieldSourceBadge
 *
 * Displays a colored badge indicating the source of a template field's value.
 * Adapted from correspondence/package-edit/CorrespondenceFieldEditor.tsx
 * to work with v2 template field types.
 */
import { cn } from '@/lib/utils';

import type { FieldValueSource } from '../utils/fieldValidation';

interface FieldSourceBadgeProps {
  source: FieldValueSource;
  isMissing?: boolean;
  inline?: boolean;
}

export const FieldSourceBadge: React.FC<FieldSourceBadgeProps> = ({
  source,
  isMissing,
  inline,
}) => {
  const baseClass = inline
    ? 'text-[9px] ml-1.5 px-1 py-0.5 rounded font-medium'
    : 'text-xs ml-2 px-1.5 py-0.5 rounded font-medium';

  if (isMissing) {
    return (
      <span className={cn(baseClass, 'bg-red-100 text-red-700')}>missing</span>
    );
  }

  if (source === 'empty') return null;

  switch (source) {
    case 'override':
      return (
        <span className={cn(baseClass, 'bg-violet-100 text-violet-700')}>
          override
        </span>
      );
    case 'context':
      return (
        <span className={cn(baseClass, 'bg-blue-100 text-blue-600')}>
          from matter
        </span>
      );
    case 'default':
      return (
        <span className={cn(baseClass, 'bg-gray-100 text-gray-600')}>
          default
        </span>
      );
    default:
      return null;
  }
};
